import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContentEditor } from '@/components/ui/content-editor';
import { 
  Save, 
  Eye, 
  Send, 
  Calendar,
  Tag,
  FileText,
  Globe,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { supabase, BlogPost } from '@/lib/supabase';
import slugify from 'slugify';

interface BlogPostEditorProps {
  post?: BlogPost;
  onSave?: (post: BlogPost) => void;
  onCancel?: () => void;
  onPreview?: (post: BlogPost) => void;
}

interface BlogPostFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  meta_title?: string;
  meta_description?: string;
  featured_image?: string;
  author?: string;
  category?: string;
  tags: string[];
  published_at?: string;
  scheduled_for?: string;
}

const createBlogPost = async (data: Partial<BlogPost>): Promise<BlogPost> => {
  const { data: result, error } = await supabase
    .from('blog_posts')
    .insert([{
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

const updateBlogPost = async (id: string, data: Partial<BlogPost>): Promise<BlogPost> => {
  const { data: result, error } = await supabase
    .from('blog_posts')
    .update({
      ...data,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const BlogPostEditor: React.FC<BlogPostEditorProps> = ({
  post,
  onSave,
  onCancel,
  onPreview
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!post;

  const [formData, setFormData] = useState<BlogPostFormData>({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    status: post?.status || 'draft',
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    featured_image: post?.featured_image || '',
    author: post?.author || 'Admin User',
    category: post?.category || '',
    tags: post?.tags || [],
    published_at: post?.published_at || '',
    scheduled_for: post?.scheduled_for || ''
  });

  const [tagInput, setTagInput] = useState('');
  const [seoScore, setSeoScore] = useState(85);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !isEditing) {
      const newSlug = slugify(formData.title, { lower: true, strict: true });
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  }, [formData.title, isEditing]);

  // Auto-generate meta title if not set
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData(prev => ({ ...prev, meta_title: formData.title }));
    }
  }, [formData.title, formData.meta_title]);

  // Calculate SEO score (simplified)
  useEffect(() => {
    let score = 0;
    if (formData.title.length >= 30 && formData.title.length <= 60) score += 20;
    if (formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160) score += 20;
    if (formData.excerpt && formData.excerpt.length >= 100) score += 15;
    if (formData.content && formData.content.length >= 300) score += 15;
    if (formData.featured_image) score += 10;
    if (formData.tags.length >= 3) score += 10;
    if (formData.slug && formData.slug.length <= 60) score += 10;
    setSeoScore(score);
  }, [formData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: BlogPostFormData) => {
      const blogData = {
        ...data,
        tags: data.tags,
        word_count: data.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length,
        reading_time: Math.ceil(data.content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200)
      };

      if (isEditing && post) {
        return updateBlogPost(post.id, blogData);
      } else {
        return createBlogPost(blogData);
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      if (onSave) onSave(result);
    },
    onError: (error) => {
      console.error('Error saving blog post:', error);
    }
  });

  const handleInputChange = (field: keyof BlogPostFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (formData.status === 'published' && !formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required for published posts';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (status?: BlogPostFormData['status']) => {
    const dataToSave = status ? { ...formData, status } : formData;
    
    if (validateForm()) {
      // Set published_at if publishing
      if (status === 'published' && !dataToSave.published_at) {
        dataToSave.published_at = new Date().toISOString();
      }
      
      saveMutation.mutate(dataToSave);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview({
        ...formData,
        id: post?.id || 'preview',
        created_at: post?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        word_count: formData.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length,
        reading_time: Math.ceil(formData.content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200)
      } as BlogPost);
    }
  };

  const getSeoScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeoScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (score >= 60) return <Clock className="h-4 w-4 text-yellow-600" />;
    return <AlertCircle className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update your blog post content and settings.' : 'Write and publish a new blog post for your website.'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSave('draft')}
            disabled={saveMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button 
            onClick={() => handleSave('published')}
            disabled={saveMutation.isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            Publish
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Post Content</CardTitle>
              <CardDescription>
                Create engaging content for your blog readers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Post Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter an engaging post title..."
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={errors.title ? 'border-red-500' : ''}
                />
                {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
                <p className="text-xs text-muted-foreground">
                  {formData.title.length}/60 characters (recommended: 30-60)
                </p>
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <Input
                  id="slug"
                  placeholder="url-friendly-slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  className={errors.slug ? 'border-red-500' : ''}
                />
                {errors.slug && <p className="text-sm text-red-600">{errors.slug}</p>}
                <p className="text-xs text-muted-foreground">
                  URL: /blog/{formData.slug}
                </p>
              </div>

              {/* Excerpt */}
              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  placeholder="Write a compelling excerpt that summarizes your post..."
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  rows={3}
                  className={errors.excerpt ? 'border-red-500' : ''}
                />
                {errors.excerpt && <p className="text-sm text-red-600">{errors.excerpt}</p>}
                <p className="text-xs text-muted-foreground">
                  {formData.excerpt.length}/160 characters (shown in search results)
                </p>
              </div>

              {/* Content Editor */}
              <div className="space-y-2">
                <ContentEditor
                  label="Post Content *"
                  description="Write your blog post content with rich formatting, images, and media."
                  value={formData.content}
                  onChange={(content) => handleInputChange('content', content)}
                  placeholder="Start writing your blog post content..."
                  height={500}
                  showPreview={true}
                  showRawHtml={true}
                  error={errors.content}
                />
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
              <CardDescription>
                Optimize your post for search engines and social media.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Meta Title */}
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title</Label>
                <Input
                  id="meta_title"
                  placeholder="SEO title for search engines..."
                  value={formData.meta_title}
                  onChange={(e) => handleInputChange('meta_title', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.meta_title?.length || 0}/60 characters
                </p>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  placeholder="Describe your post for search engine results..."
                  value={formData.meta_description}
                  onChange={(e) => handleInputChange('meta_description', e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.meta_description?.length || 0}/160 characters
                </p>
              </div>

              {/* Featured Image */}
              <div className="space-y-2">
                <Label htmlFor="featured_image">Featured Image URL</Label>
                <Input
                  id="featured_image"
                  placeholder="https://example.com/image.jpg"
                  value={formData.featured_image}
                  onChange={(e) => handleInputChange('featured_image', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Will be used as the main image for the post and social sharing
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publishing Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Author */}
              <div className="space-y-2">
                <Label>Author</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => handleInputChange('author', e.target.value)}
                  placeholder="Author name"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masonry-tips">Masonry Tips</SelectItem>
                    <SelectItem value="home-maintenance">Home Maintenance</SelectItem>
                    <SelectItem value="project-showcase">Project Showcase</SelectItem>
                    <SelectItem value="industry-news">Industry News</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule */}
              {formData.status === 'scheduled' && (
                <div className="space-y-2">
                  <Label>Scheduled For</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_for}
                    onChange={(e) => handleInputChange('scheduled_for', e.target.value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button size="sm" onClick={handleAddTag}>
                  <Tag className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* SEO Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                {getSeoScoreIcon(seoScore)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Overall Score</span>
                    <span className={`font-bold ${getSeoScoreColor(seoScore)}`}>
                      {seoScore}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        seoScore >= 80 ? 'bg-green-500' : 
                        seoScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${seoScore}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Title Length</span>
                  <span className={formData.title.length >= 30 && formData.title.length <= 60 ? 'text-green-600' : 'text-red-600'}>
                    {formData.title.length >= 30 && formData.title.length <= 60 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Meta Description</span>
                  <span className={formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? 'text-green-600' : 'text-red-600'}>
                    {formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Content Length</span>
                  <span className={formData.content && formData.content.length >= 300 ? 'text-green-600' : 'text-red-600'}>
                    {formData.content && formData.content.length >= 300 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Featured Image</span>
                  <span className={formData.featured_image ? 'text-green-600' : 'text-red-600'}>
                    {formData.featured_image ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Tags (3+)</span>
                  <span className={formData.tags.length >= 3 ? 'text-green-600' : 'text-red-600'}>
                    {formData.tags.length >= 3 ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Words</span>
                <span className="font-medium">
                  {formData.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Characters</span>
                <span className="font-medium">
                  {formData.content.replace(/<[^>]*>/g, '').length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Reading Time</span>
                <span className="font-medium">
                  {Math.ceil(formData.content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200)} min
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;